/**
 * Product Redux Slice
 * Secure and optimized state management for product data
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ProductStatus } from '@Constants/product.constants';
import { ProductDetails, ProductFilters, ProductState, RootState } from '../types';
import { ProductApiService } from '../api/productApi';

// Initial state
const initialState: ProductState = {
    products: [],
    selectedProduct: null,
    suppliers: {
        list: [],
        loading: false,
        error: null,
    },
    outreachEmail: {
        subject: '',
        body: '',
        loading: false,
        error: null
    },
    loading: true,
    productLoading: false,
    error: null,
    filters: {
        status: ProductStatus.Active,
        sort_by: 'created_at',
        sort_desc: true,
    },
    pagination: {
        limit: 20,
        offset: 0,
        total: 0,
    },
};

// Async thunks
export const fetchProducts = createAsyncThunk(
    'products/fetchProducts',
    async (params?: { filters?: ProductFilters; pagination?: { offset: number; limit: number } }) => {
        const { filters = initialState.filters, pagination = { offset: 0, limit: 20 } } = params || {};

        const queryParams = {
            ...filters,
            limit: pagination.limit,
            offset: pagination.offset,
        };

        const result = await ProductApiService.getProducts(queryParams);
        return {
            products: result.products,
            total: result.total,
            offset: pagination.offset,
            limit: pagination.limit,
        };
    }
);

export const fetchProductById = createAsyncThunk(
    'products/fetchProductById',
    async (productId: string | number) => {
        return await ProductApiService.getProductById(productId);
    }
);

export const createProduct = createAsyncThunk(
    'products/createProduct',
    async (productData: Partial<Omit<ProductDetails, 'id' | 'user_id' | 'created_at' | 'updated_at'>>) => {
        return await ProductApiService.createProduct(productData);
    }
);

export const updateProduct = createAsyncThunk(
    'products/updateProduct',
    async (params: {
        productId?: string | number;
        productData: Partial<Omit<ProductDetails, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;
    }, { getState }) => {
        const state = getState() as RootState;
        const { productId, productData } = params;

        const actualProductId = productId || state.products.selectedProduct?.id;

        if (!actualProductId) {
            throw new Error('No product ID provided and no selected product found');
        }

        return await ProductApiService.updateProduct(actualProductId, productData);
    }
);


export const uploadProductImages = createAsyncThunk(
    'products/uploadProductImages',
    async (params: { productId: number | string, imageFiles: File[] }) => {
        const { productId, imageFiles } = params;
        return await ProductApiService.uploadProductImages(productId, imageFiles);
    }
);

export const deleteProductImage = createAsyncThunk(
    'products/deleteProductImage',
    async (params: { productId: number | string, imageId: number | string }) => {
        const { productId, imageId } = params;
        return await ProductApiService.deleteProductImage(productId, imageId);
    }
);

export const activateProduct = createAsyncThunk(
    'products/activateProduct',
    async (productId: string | number) => {
        return await ProductApiService.updateProductStatus(productId, 'Active');
    }
);

export const deactivateProduct = createAsyncThunk(
    'products/deactivateProduct',
    async (productId: string | number) => {
        return await ProductApiService.updateProductStatus(productId, 'Inactive');
    }
);

export const archiveProduct = createAsyncThunk(
    'products/archiveProduct',
    async (productId: string | number) => {
        return await ProductApiService.updateProductStatus(productId, 'Archived');
    }
);

export const unarchiveProduct = createAsyncThunk(
    'products/unarchiveProduct',
    async (productId: string | number) => {
        return await ProductApiService.updateProductStatus(productId, 'Active');
    }
);

export const fetchProductOutreachEmail = createAsyncThunk(
    'products/fetchProductOutreachEmail',
    async (productId: string | number) => {
        return await ProductApiService.getProductOutreachEmail(productId);
    }
);

export const updateProductOutreachEmail = createAsyncThunk(
    'products/updateProductOutreachEmail',
    async (params: {
        productId: string | number;
        emailData: {subject: string, body: string};
    }) => {
        const { productId, emailData } = params;
        return await ProductApiService.updateProductOutreachEmail(productId, emailData);
    }
);

export const applyFilters = createAsyncThunk(
    'products/applyFilters',
    async (newFilters: Partial<ProductFilters>, { getState, dispatch }) => {
        const state = getState() as RootState;
        const { filters, pagination } = state.products;

        dispatch(updateFilters(newFilters));

        const updatedFilters = { ...filters, ...newFilters };
        return await ProductApiService.getProducts({
            ...updatedFilters,
            limit: pagination.limit,
            offset: 0,
        });
    }
);

// Product slice
export const productSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {
        // Update filters
        updateFilters: (state, action: PayloadAction<Partial<ProductFilters>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },

        // Clear filters
        clearFilters: (state) => {
            state.filters = { ...initialState.filters };
        },

        // Clear error
        clearError: (state) => {
            state.error = null;
        },

        // Clear selected product
        clearSelectedProduct: (state) => {
            state.selectedProduct = null;
            state.productLoading = false;
        },

        // Reset products state
        resetProducts: () => initialState,
    },
    extraReducers: (builder) => {
        // Fetch products
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.products;
                state.pagination = {
                    ...state.pagination,
                    limit: action.payload.limit,
                    offset: action.payload.offset,
                    total: action.payload.total,
                };
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch products';
            });

        // Fetch product by ID
        builder
            .addCase(fetchProductById.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                state.productLoading = false;
                state.selectedProduct = action.payload;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.productLoading = false;
                state.error = action.error.message || 'Failed to fetch product details';
            });

        // Create product
        builder
            .addCase(createProduct.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(createProduct.fulfilled, (state, action) => {
                state.productLoading = false;
                state.selectedProduct = action.payload;
                // Add the newly created product to the list if it matches current filters
                if (state.products.length > 0) {
                    state.products = [action.payload, ...state.products];
                    state.pagination.total += 1;
                }
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.productLoading = false;
            });

        // Update product
        builder
            .addCase(updateProduct.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(updateProduct.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.productLoading = false;
            });

        // Upload product images
        builder
            .addCase(uploadProductImages.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(uploadProductImages.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(uploadProductImages.rejected, (state, action) => {
                state.productLoading = false;
            });

        // apply filter product list
        builder
            .addCase(applyFilters.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.products;
                state.pagination = {
                    ...state.pagination,
                    offset: 0,
                    total: action.payload.total,
                };
            });

        // Delete product image
        builder
            .addCase(deleteProductImage.pending, (state) => {
                state.productLoading = true;
            })
            .addCase(deleteProductImage.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(deleteProductImage.rejected, (state, action) => {
                state.productLoading = false;
            });

        // TODO LIST NOT RERENDER CHECK LIST KEY
        // Activate product
        builder
            .addCase(activateProduct.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(activateProduct.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(activateProduct.rejected, (state, action) => {
                state.productLoading = false;
            });

        // Deactivate product
        builder
            .addCase(deactivateProduct.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(deactivateProduct.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(deactivateProduct.rejected, (state, action) => {
                state.productLoading = false;
            });

        // Archive product
        builder
            .addCase(archiveProduct.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(archiveProduct.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(archiveProduct.rejected, (state, action) => {
                state.productLoading = false;
            });

        // Unarchive product
        builder
            .addCase(unarchiveProduct.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(unarchiveProduct.fulfilled, (state, action) => {
                state.productLoading = false;
                const updatedProduct = action.payload;
                state.products = state.products.map((product) =>
                    product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
                );
                if (state.selectedProduct?.id === updatedProduct.id) {
                    state.selectedProduct = updatedProduct;
                }
            })
            .addCase(unarchiveProduct.rejected, (state, action) => {
                state.productLoading = false;
            });

        // Fetch product outreach email
        builder
            .addCase(fetchProductOutreachEmail.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(fetchProductOutreachEmail.fulfilled, (state, action) => {
                state.productLoading = false;
                const { subject, body } = action.payload;
                state.outreachEmail.subject = subject;
                state.outreachEmail.body = body;
            })
            .addCase(fetchProductOutreachEmail.rejected, (state, action) => {
                state.productLoading = false;
                state.error = action.error.message || 'Failed to fetch product outreach email';
            });

        // Update product outreach email
        builder
            .addCase(updateProductOutreachEmail.pending, (state) => {
                state.productLoading = true;
                state.error = null;
            })
            .addCase(updateProductOutreachEmail.fulfilled, (state, action) => {
                state.productLoading = false;
                const { subject, body } = action.payload;
                state.outreachEmail.subject = subject;
                state.outreachEmail.body = body;
            })
            .addCase(updateProductOutreachEmail.rejected, (state, action) => {
                state.productLoading = false;
                state.error = action.error.message || 'Failed to update product outreach email';
            });
    },
});

// Export actions
export const {
    updateFilters,
    clearFilters,
    clearError,
    clearSelectedProduct,
    resetProducts,
} = productSlice.actions;

// Selectors
export const selectProducts = (state: { products: ProductState }) => state.products.products;
export const selectSelectedProduct = (state: { products: ProductState }) => state.products.selectedProduct;
export const selectProductsLoading = (state: { products: ProductState }) => state.products.loading;
export const selectProductLoading = (state: { products: ProductState }) => state.products.productLoading;
export const selectProductsError = (state: { products: ProductState }) => state.products.error;
export const selectProductsFilters = (state: { products: ProductState }) => state.products.filters;
export const selectProductsPagination = (state: { products: ProductState }) => state.products.pagination;
export const selectOutreachEmail = (state: { products: ProductState }) => state.products.outreachEmail;